import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';

import styles from './ProfileSideBar.module.css';
import EmployerProfilePlanUi from '../EmployerProfilePlanUi/EmployerProfilePlanUi';
import { useNavigate, useLocation } from 'react-router-dom';
import { getJobSeekerList, getFavoriteTalents, getTopJobSeekerLists, getAllListCounts } from '../../../../services/TalentsService';

function ProfileSideBar({ activeTab, setActiveTab, title, description, description2, tabs, removeSubscriptionInformation, employerPortal, ...props }) {
    const { t } = useTranslation();
    const user = useSelector(state => state.user.user);
    const navigate = useNavigate();
    const location = useLocation();
    const [listOptions, setListOptions] = useState([]);
    const [topLists, setTopLists] = useState([]);
    const [otherLists, setOtherLists] = useState([]);
    const [selectedList, setSelectedList] = useState(null);
    const [listCounts, setListCounts] = useState({});
    const [showOtherLists, setShowOtherLists] = useState(false);
    const [isOtherListSelected, setIsOtherListSelected] = useState(false);
    
    // Get the list parameters from URL
    const queryParams = new URLSearchParams(location.search);
    const selectedListName = queryParams.get('list');
    const selectedListId = queryParams.get('listId');
    
    // Update selected list when URL changes
    useEffect(() => {
        if (selectedListId) {
            const selectedId = parseInt(selectedListId);
            setSelectedList(selectedId);
            
            // Check if the selected list is in the "other lists" section
            const isInTopLists = topLists.some(list => list.id === selectedId);
            const isInOtherLists = otherLists.some(list => list.id === selectedId);
            
            if (isInOtherLists && !isInTopLists) {
                setIsOtherListSelected(true);
                setShowOtherLists(true); // Automatically expand the other lists section
            } else {
                setIsOtherListSelected(false);
            }
        } else {
            setSelectedList(null);
            setIsOtherListSelected(false);
        }
    }, [selectedListId, topLists, otherLists]);
    
    // Fetch favorite lists
    useEffect(() => {
        if (user && user.id && activeTab === 1 && employerPortal) {
            const fetchTopLists = async () => {
                try {
                    // Use the new API to get top lists with counts in a single call
                    const response = await getTopJobSeekerLists(user.id);
                    
                    // Also fetch all lists to get the ones that aren't in the top 3
                    const allListsResponse = await getJobSeekerList(user.id);
                    const allLists = allListsResponse.data.favoriteLists;
                    
                    // Fetch counts for all lists
                    const listCountsResponse = await getAllListCounts(user.id);
                    const listsWithCounts = listCountsResponse.lists;
                    
                    // Create a map of list IDs to their counts
                    const countsMap = {};
                    listsWithCounts.forEach(list => {
                        countsMap[list.id] = list.talent_count;
                    });
                    
                    if (response && response.topLists) {
                        // Get the top lists (usually 3)
                        setTopLists(response.topLists);
                        
                        // Set list counts
                        setListCounts(countsMap);
                        
                        // Find lists that aren't in the top lists
                        const topListIds = response.topLists.map(list => list.id);
                        const remainingLists = allLists.filter(list => !topListIds.includes(list.id));
                        
                        // Set other lists with their counts
                        setOtherLists(remainingLists.map(list => ({
                            ...list,
                            talent_count: countsMap[list.id] || 0
                        })));
                        
                        // Also set listOptions for backward compatibility
                        setListOptions(allLists.map((item) => ({
                            id: item.id,
                            label: item.list_name,
                            value: item.list_name,
                            count: countsMap[item.id] || 0
                        })));
                        
                        // If we have a selected list ID from URL, check if it's in the "other lists" section
                        if (selectedListId) {
                            const selectedId = parseInt(selectedListId);
                            const isInTopLists = response.topLists.some(list => list.id === selectedId);
                            const isInOtherLists = remainingLists.some(list => list.id === selectedId);
                            
                            if (isInOtherLists && !isInTopLists) {
                                setIsOtherListSelected(true);
                                setShowOtherLists(true); // Automatically expand the other lists section
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error fetching lists:", error);
                }
            };

            fetchTopLists();
        }
    }, [user, activeTab, employerPortal, selectedListId]);
    
    // Listen for list selection changes from the dropdown in EmployerPortalCandidates
    useEffect(() => {
        const handleDropdownListSelected = (event) => {
            const selectedListData = event.detail;
            if (selectedListData && selectedListData.id) {
                const listId = parseInt(selectedListData.id);
                setSelectedList(listId);
                
                // Check if the selected list is in the top lists or other lists
                const isInTopLists = topLists.some(list => list.id === listId);
                const isInOtherLists = otherLists.some(list => list.id === listId);
                
                if (isInOtherLists && !isInTopLists) {
                    setIsOtherListSelected(true);
                    setShowOtherLists(true); // Automatically expand the other lists section
                } else {
                    setIsOtherListSelected(false);
                }
            }
        };
        
        const handleDropdownListCleared = () => {
            setSelectedList(null);
            setIsOtherListSelected(false);
        };
        
        // Add event listeners
        window.addEventListener('dropdownListSelected', handleDropdownListSelected);
        window.addEventListener('dropdownListCleared', handleDropdownListCleared);
        
        // Cleanup function
        return () => {
            window.removeEventListener('dropdownListSelected', handleDropdownListSelected);
            window.removeEventListener('dropdownListCleared', handleDropdownListCleared);
        };
    }, [topLists, otherLists]);
    
    // Listen for jobseeker list updates from CreateFavListForm
    useEffect(() => {
        const handleJobseekerListsUpdated = async (event) => {
            // Only refresh lists if we're on the candidates tab
            if (activeTab === 1 && employerPortal && user && user.id) {
                try {
                    // Fetch updated top lists
                    const response = await getTopJobSeekerLists(user.id);
                    
                    // Fetch all lists to get the ones that aren't in the top 3
                    const allListsResponse = await getJobSeekerList(user.id);
                    const allLists = allListsResponse.data.favoriteLists;
                    
                    // Fetch counts for all lists
                    const listCountsResponse = await getAllListCounts(user.id);
                    const listsWithCounts = listCountsResponse.lists;
                    
                    // Create a map of list IDs to their counts
                    const countsMap = {};
                    listsWithCounts.forEach(list => {
                        countsMap[list.id] = list.talent_count;
                    });
                    
                    if (response && response.topLists) {
                        // Update top lists
                        setTopLists(response.topLists);
                        
                        // Update list counts
                        setListCounts(countsMap);
                        
                        // Find lists that aren't in the top lists
                        const topListIds = response.topLists.map(list => list.id);
                        const remainingLists = allLists.filter(list => !topListIds.includes(list.id));
                        
                        // Update other lists with their counts
                        setOtherLists(remainingLists.map(list => ({
                            ...list,
                            talent_count: countsMap[list.id] || 0
                        })));
                        
                        // Update listOptions for backward compatibility
                        setListOptions(allLists.map((item) => ({
                            id: item.id,
                            label: item.list_name,
                            value: item.list_name,
                            count: countsMap[item.id] || 0
                        })));
                        
                        // If we have a selected list ID, check if it's still valid
                        if (selectedList) {
                            const selectedId = parseInt(selectedList);
                            const isInAnyList = allLists.some(list => list.id === selectedId);
                            
                            if (!isInAnyList) {
                                // If the list no longer exists, clear the selection
                                setSelectedList(null);
                                setIsOtherListSelected(false);
                            } else {
                                // Otherwise, update the "other lists" status
                                const isInTopLists = response.topLists.some(list => list.id === selectedId);
                                const isInOtherLists = remainingLists.some(list => list.id === selectedId);
                                
                                if (isInOtherLists && !isInTopLists) {
                                    setIsOtherListSelected(true);
                                    setShowOtherLists(true); // Automatically expand the other lists section
                                } else {
                                    setIsOtherListSelected(false);
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error refreshing lists after update:", error);
                }
            }
        };
        
        // Add event listener
        window.addEventListener('jobseekerListsUpdated', handleJobseekerListsUpdated);
        
        // Cleanup function
        return () => {
            window.removeEventListener('jobseekerListsUpdated', handleJobseekerListsUpdated);
        };
    }, [activeTab, employerPortal, user, selectedList]);

    const handleTabClick = (index) => {
        if (employerPortal) {
            // Clear list selection when switching tabs
            setSelectedList(null);
            setIsOtherListSelected(false);
            const navigateRoute = index == 1 ? `/employer-portal/candidates` : index == 2 ? '/employer-portal/applicants' : '/employer-portal/jobs'
            navigate(navigateRoute)
        } else {
            const tabs = [
                { path: '/profile/personal-information', },
                { path: '/profile/contact-information', },
                { path: '/profile/work-experience', },
                { path: '/profile/education', },
                { path: '/profile/skills', },
                { path: '/profile/occupation', },
                { path: '/profile/job-preferences', },
                { path: '/profile/resume', },
                { path: '/profile/certification', },
                { path: '/profile/portfolio', },
                { path: `/profile/account`, }
            ];
            if (user.account_type == 'employer') {
                if (user.employer_status == 'business') {
                    tabs.splice(2, 0, { path: `/profile/company-information`, }, { path: `/profile/account`, })
                } else {
                    tabs.splice(2, 0, { path: `/profile/account`, })
                }
            }
            const route = tabs[index].path;
            navigate(route);
        }
    };

    const handleListClick = (list) => {
        setSelectedList(list.id);
        
        // Check if the clicked list is in the "other lists" section
        const isInTopLists = topLists.some(topList => topList.id === list.id);
        setIsOtherListSelected(!isInTopLists);
        
        // Pass both the list ID and name in the URL parameters
        navigate(`/employer-portal/candidates?listId=${list.id}&list=${encodeURIComponent(list.list_name)}`);
        
        // Dispatch a custom event to notify EmployerPortalCandidates component
        const listChangeEvent = new CustomEvent('listSelectionChanged', {
            detail: {
                id: list.id,
                value: list.list_name,
                label: list.list_name
            }
        });
        window.dispatchEvent(listChangeEvent);
    };

    // Handle clearing the list selection
    const handleClearListSelection = () => {
        setSelectedList(null);
        setIsOtherListSelected(false);
        navigate('/employer-portal/candidates');
        
        // Dispatch a custom event to notify EmployerPortalCandidates component
        const clearListEvent = new CustomEvent('listSelectionCleared');
        window.dispatchEvent(clearListEvent);
    };
    
    // Toggle showing other lists
    const toggleOtherLists = () => {
        setShowOtherLists(!showOtherLists);
    };

    let price = '$95';
    let price_reccuring = '$65';

    if (user?.subscription_price?.currency_options) {
        if (user.country === 'AE') {
            price = (Math.ceil(parseFloat(user?.subscription_price?.currency_options?.aed?.unit_amount_decimal) / 100) + 111) + 'د.إ(AED)';
            price_reccuring = (Math.ceil(parseFloat(user?.subscription_price?.currency_options?.aed?.unit_amount_decimal) / 100)) + 'د.إ(AED)';
        }
        else if (user.country === 'SA') {
            price = (Math.ceil(parseFloat(user?.subscription_price?.currency_options?.sar?.unit_amount_decimal) / 100) + 111) + 'ر.س(SAR)';
            price_reccuring = (Math.ceil(parseFloat(user?.subscription_price?.currency_options?.sar?.unit_amount_decimal) / 100)) + 'ر.س(SAR)';
        }
        else if (user.country === 'QA') {
            price = (Math.ceil(parseFloat(user?.subscription_price?.currency_options?.qar?.unit_amount_decimal) / 100) + 111) + 'درهم(QAR)';
            price_reccuring = (Math.ceil(parseFloat(user?.subscription_price?.currency_options?.qar?.unit_amount_decimal) / 100)) + 'درهم(QAR)';
        }
    }

    return (
        <div className={styles.sideBarContainer} {...props}>
            <div className={styles.tabsContainer}>
                <span className={styles.sideBarTitleContainer}>
                    <p className={styles.sideBarTitle}>{title ? title : t('profileSidebar.title')}</p>
                    <p className={styles.sideBarDesc}>{description ? description : t('profileSidebar.description')}</p>
                    {description2 && <p className={styles.sideBarDesc}>{description2}</p>}
                </span>
                <div className={styles.listContaienr}>
                    {tabs.map((tab, index) => {
                        return (
                            <div key={index}>
                                <div
                                    className={`${styles.tab} ${activeTab === index ? styles.activeTab : ''}`}
                                    onClick={() => handleTabClick(index)}
                                >
                                    <img
                                        src={tab.icon}
                                        alt={tab.name}
                                        className={`${styles.tabIcon} ${activeTab === index ? styles.activeTabIcon : ''}`}
                                    />
                                    <span className={`${styles.tabText} ${activeTab === index ? styles.activeTabText : ''}`}>
                                        {t(`profileSidebar.tabs.${tab.name}`)}
                                    </span>
                                </div>

                                {/* Show subtabs for candidates tab when active */}
                                {activeTab === index && index === 1 && employerPortal && (topLists.length > 0 || otherLists.length > 0) && (
                                    <div className={styles.subTabsContainer}>
                                        {selectedList && (
                                            <div 
                                                className={styles.clearListSelection}
                                                onClick={handleClearListSelection}
                                            >
                                                {t('profileSidebar.clearListSelection') || '← Back to all candidates'}
                                            </div>
                                        )}
                                        {/* Top lists */}
                                        {topLists.map((list) => (
                                            <div
                                                key={list.id}
                                                className={`${styles.subTab} ${selectedList === list.id ? styles.activeSubTab : ''}`}
                                                onClick={() => handleListClick(list)}
                                            >
                                                <span className={styles.subTabText}>
                                                    {list.list_name} <span className={styles.countBadge}>({list.talent_count || 0})</span>
                                                </span>
                                            </div>
                                        ))}
                                        
                                        {/* Other lists section */}
                                        {otherLists.length > 0 && (
                                            <>
                                                <div 
                                                    className={`${styles.subTab} ${styles.otherListsToggle} ${isOtherListSelected ? styles.activeSubTab : ''}`}
                                                    onClick={toggleOtherLists}
                                                >
                                                    <span className={styles.subTabText}>
                                                        {t('profileSidebar.otherLists') || 'Other Lists'} 
                                                        <span className={styles.countBadge}>({otherLists.length})</span>
                                                        <span className={`${styles.toggleIcon} ${showOtherLists ? styles.expanded : ''}`}>
                                                            {showOtherLists ? '▼' : '▶'}
                                                        </span>
                                                    </span>
                                                </div>
                                                
                                                {/* Expandable section for other lists */}
                                                {showOtherLists && (
                                                    <div className={styles.otherListsContainer}>
                                                        {otherLists.map((list) => (
                                                            <div
                                                                key={list.id}
                                                                className={`${styles.subTab} ${styles.otherSubTab} ${selectedList === list.id ? styles.activeSubTab : ''}`}
                                                                onClick={() => handleListClick(list)}
                                                            >
                                                                <span className={styles.subTabText}>
                                                                    {list.list_name} <span className={styles.countBadge}>({list.talent_count || 0})</span>
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
            {!removeSubscriptionInformation && user.account_type !== 'jobseeker' && user.subscriptionStatus == 'active' && <EmployerProfilePlanUi />}
        </div>
    );
}

export default ProfileSideBar;




